import { Component, OnInit, OnDestroy, inject, PLATFORM_ID, NgZone, DestroyRef } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { NavbarComponent } from './features/home/components/navbar/navbar.component';
import { FooterComponent } from './features/home/components/footer/footer.component';
import { AnnouncementBarComponent } from './features/home/components/announcement-bar/announcement-bar.component';
import { ConsentBannerComponent } from './shared/components/consent-banner/consent-banner.component';
import { AnalyticsService } from './core/services/analytics.service';
import { ConsentService } from './core/services/consent.service';
import { ScrollRestorationService } from './core/services/scroll-restoration.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NavbarComponent, FooterComponent, AnnouncementBarComponent, ConsentBannerComponent],
  template: `
    <a class="skip-link" href="#main-content">Skip to main content</a>
    <app-navbar />
    <app-announcement-bar />
    <main id="main-content" tabindex="-1">
      <router-outlet />
    </main>
    <app-footer />
    <app-consent-banner />
  `,
  styles: [`
    :host { display: block; }
    /* Compensate for fixed navbar (was sticky — sticky broke under mobile-menu scroll-lock). */
    main { padding-top: var(--nav-height); }
    .skip-link {
      position: absolute;
      top: -100%;
      left: var(--space-4);
      z-index: 9999;
      background: var(--color-primary);
      color: var(--color-text-inverse);
      padding: var(--space-2) var(--space-4);
      border-radius: var(--radius-md);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-semibold);
      text-decoration: none;
      transition: top var(--transition-fast);
      &:focus { top: var(--space-2); }
    }
  `]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly _consent = inject(ConsentService); // Initializes Consent Mode v2 defaults before gtag
  private readonly analytics = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly scrollRestoration = inject(ScrollRestorationService);

  private scrollHandler: (() => void) | null = null;
  private firedDepths = new Set<number>();

  ngOnInit(): void {
    // Back/Forward returns to where the visitor was (MV-390). Runs before the analytics early return below.
    if (isPlatformBrowser(this.platformId)) this.scrollRestoration.init();
    const id = environment.gaMeasurementId;
    if (!id || !isPlatformBrowser(this.platformId)) return;

    // Inject gtag script
    const script = this.doc.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    this.doc.head.appendChild(script);

    const debugFlag = environment.gaDebugMode ? ", 'debug_mode': true" : '';
    const inline = this.doc.createElement('script');
    inline.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${id}', { 'send_page_view': false${debugFlag} });
    `;
    this.doc.head.appendChild(inline);

    // Session start (once)
    this.analytics.trackSessionStart();

    // Page views on route change
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((e) => {
      this.analytics.trackPageView(e.urlAfterRedirects, this.doc.title);
      this.firedDepths.clear();
    });

    // Scroll depth tracking
    this.setupScrollDepth();
  }

  ngOnDestroy(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }

  private setupScrollDepth(): void {
    let ticking = false;
    this.scrollHandler = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const win = this.doc.defaultView;
        if (!win) return;
        const scrollTop = win.scrollY;
        const docHeight = this.doc.documentElement.scrollHeight - win.innerHeight;
        if (docHeight <= 0) return;
        const pct = (scrollTop / docHeight) * 100;
        const thresholds = [25, 50, 75, 100] as const;
        for (const t of thresholds) {
          if (pct >= t && !this.firedDepths.has(t)) {
            this.firedDepths.add(t);
            this.zone.run(() => this.analytics.trackScrollDepth(t, this.router.url));
          }
        }
      });
    };
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }
}
